import { Injectable } from '@angular/core';
import { TargetingRule } from '../uas/editing/targeting/targeting-rule.interface';

type TimeParts = {
  hour?: string | number;
  minute?: string | number;
};

@Injectable({
  providedIn: 'root'
})
export class TargetingRuleValidatorService {
  validateRule(rule: TargetingRule): boolean {
    if (!rule?.template_id || !rule?.option_type_id || !rule?.operator_id) {
      return false;
    }

    switch (rule.option_type_id) {
      case 'display_dates':
      case 'not_applicable':
        return true;
      case 'duration_interval':
      case 'stopover_duration_interval':
      case 'time_interval':
        return this.validateRangeRule(rule);
      case 'date_interval':
        return this.hasValue(rule.start) && this.hasValue(rule.stop);
      case 'time':
        return this.hasValidTimeValue(rule);
      case 'origin_and_destination':
        return this.validateOriginAndDestinationRule(rule);
      case 'carriers':
        return Array.isArray(rule.carrier_codes) && rule.carrier_codes.length > 0;
      case 'equipment_class':
        return this.hasText(rule.equipment_class_value) || this.hasText(rule.option_id);
      case 'airport':
      case 'stopover_connection':
        return this.hasText(rule.airport_code);
      case 'country':
        return this.hasText(rule.country_code);
      case 'terminal':
        return this.hasText(rule.terminal_code);
      case 'subfleet':
        return this.hasValue(rule.subfleet_id);
      default:
        return this.hasValue(rule.option_id);
    }
  }

  private validateOriginAndDestinationRule(rule: TargetingRule): boolean {
    if (rule.operator_id.includes('in')) {
      return this.hasValue(rule.option_id);
    }

    return this.hasText(rule.from) && this.hasText(rule.to);
  }

  private validateRangeRule(rule: TargetingRule): boolean {
    const start = this.toComparableValue(rule.start);
    const stop = this.toComparableValue(rule.stop);

    if (rule.template_id === 'arrival-layover-duration' && (stop == null || stop === 0)) {
      return true;
    }

    // Pre-existing `stop > start` would have compared against a coerced 0 for an
    // unparseable start/stop (JS coerces null to 0 in `>`); treat either as invalid
    // instead, matching this file's convention elsewhere (e.g. hasValidTimeValue).
    if (start == null || stop == null) {
      return false;
    }

    return stop > start;
  }

  private hasValidTimeValue(rule: TargetingRule): boolean {
    const time = rule.value?.time;
    if (typeof time === 'string' && /^\d{2}:\d{2}$/.test(time)) {
      return true;
    }

    const hour = rule.value?.hour;
    const minute = rule.value?.minute;
    if (!this.hasValue(hour) || !this.hasValue(minute)) {
      return false;
    }

    return this.toComparableValue({ hour, minute }) !== null;
  }

  private toComparableValue(value: unknown): number | null {
    if (typeof value === 'string') {
      const match = value.match(/^(\d{1,2}):(\d{2})$/);
      if (!match) {
        return null;
      }
      return Number(match[1]) * 60 + Number(match[2]);
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (!value || typeof value !== 'object') {
      return null;
    }

    const timeParts = value as TimeParts;

    if (!this.hasValue(timeParts.hour) && !this.hasValue(timeParts.minute)) {
      return null;
    }

    const hour = Number(timeParts.hour ?? 0);
    const minute = Number(timeParts.minute ?? 0);
    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return null;
    }

    return hour * 60 + minute;
  }

  private hasText(value: unknown): boolean {
    return typeof value === 'string' && value.trim().length > 0;
  }

  private hasValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
  }
}
